import type { FC } from "react";
import { useCallback, useEffect, useRef } from "react";

import { useScript } from "@ui/hooks/useScript";

type AdultoAvProps = {
  scriptLink: string;
  scriptKey: string;
  onAvFinish: (verificationUuid: string) => void;
};

const adultoIdInput = "#adultocz-uid";

export const AdultoAv: FC<AdultoAvProps> = ({ scriptLink, scriptKey, onAvFinish }) => {
  const adultoContainerRef = useRef<HTMLDivElement>(null);

  useScript({ scriptSrc: scriptLink });

  const observeAdultoId = useCallback(
    (adultoIdInput: HTMLInputElement) => {
      const adultoIdObserver = new MutationObserver(() => {
        const adultoVerificationId = adultoIdInput.value;

        if (adultoVerificationId) {
          onAvFinish(adultoVerificationId);

          adultoIdObserver.disconnect();
        }
      });

      adultoIdObserver.observe(adultoIdInput, { attributes: true });
    },
    [onAvFinish]
  );

  const checkAdultoId = useCallback(
    (adultoIdInput: HTMLInputElement) => {
      const adultoVerificationId = adultoIdInput.value;

      if (adultoVerificationId) {
        onAvFinish(adultoVerificationId);

        return;
      }

      observeAdultoId(adultoIdInput);
    },
    [onAvFinish, observeAdultoId]
  );

  useEffect(() => {
    const adultoContainerObserver = new MutationObserver(() => {
      const avVerificationIdInput = adultoContainerRef.current?.querySelector<HTMLInputElement>(adultoIdInput);

      if (avVerificationIdInput) {
        checkAdultoId(avVerificationIdInput);
        adultoContainerObserver.disconnect();
      }
    });

    if (!adultoContainerRef.current) return;

    adultoContainerObserver.observe(adultoContainerRef.current, { childList: true });

    return () => {
      adultoContainerObserver.disconnect();
    };
  }, [checkAdultoId]);

  return <div className="adulto-cz" data-testid="adultoContainer" data-sitekey={scriptKey} ref={adultoContainerRef} />;
};
